// frontend/src/pages/Team.jsx
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useLanguageStore } from '../store/useLanguageStore';
import { useUIStore } from '../store/useUIStore';
import { LanguageSelector } from '../components/LanguageSelector';
import { apiClient } from '../api/client';
import { AppSidebar } from '../components/AppSidebar';
import { Modal } from '../components/Modal';
import { Link, useNavigate } from 'react-router-dom';

import {
  Users, UserPlus, Send, RefreshCw, ShieldCheck, ShieldOff,
  UserX, UserCheck, Loader2, Menu, X, Mail, LogOut,
  Search, ChevronLeft, ChevronRight, Pencil,
} from 'lucide-react';
import LogoHorizontal from '../assets/images/datiaFlow_horizontal.jpg';
import Isotipo from '../assets/images/datiaFlow_cuadrado.jpg';

import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';

const ROLE_LABELS = { 1: 'SuperAdmin', 2: 'Admin', 3: 'User' };
const ITEMS_PER_PAGE = 5;

export const Team = ({ user, setUser }) => {
  const { t, language, setLanguage } = useLanguageStore();
  const { openSidebar } = useUIStore();
  const navigate = useNavigate();

  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [clientName, setClientName] = useState('');

  // Filters & pagination
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);

  // Invite modal
  const [showInvite, setShowInvite] = useState(false);
  const [inviting, setInviting] = useState(false);
  const [inviteError, setInviteError] = useState('');

  // Edit name + language modal
  const [editingUser, setEditingUser] = useState(null);
  const [editName, setEditName] = useState('');
  const [editLang, setEditLang] = useState('ES');
  const [editSaving, setEditSaving] = useState(false);
  const [availableLanguages, setAvailableLanguages] = useState([]);

  // Confirmation modal
  const [confirmAction, setConfirmAction] = useState(null);
  const [confirmUser, setConfirmUser] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  const isAdmin = Number(user?.role) === 1 || Number(user?.role) === 2;

  // ── Zod schema for invite (i18n) ─────────────────────────────────
  const inviteSchema = useMemo(() => z.object({
    name: z.string().min(2, t('common.validation_error') || 'Campo obligatorio'),
    email: z.string().email(t('login.errors.invalid_email_format') || 'Correo inválido'),
    idRole: z.number(),
  }), [t, language]);

  const {
    register, handleSubmit, reset: resetInviteForm,
    formState: { errors: formErrors },
  } = useForm({
    resolver: zodResolver(inviteSchema),
    defaultValues: { name: '', email: '', idRole: 3 },
  });

  // ── Logout ───────────────────────────────────────────────────────
  const handleLogout = () => {
    localStorage.removeItem('vnext_auth_token');
    localStorage.removeItem('vnext_user');
    setUser(null);
    navigate('/login');
  };

  // ── Fetch client name (same source as Dashboard) ─────────────────
  useEffect(() => {
    apiClient.get('/dashboard/pipelines').then(res => {
      if (res.data.clientName) setClientName(res.data.clientName);
    }).catch(() => { });
    // Cargar idiomas disponibles
    apiClient.get('/catalogs/languages').then(res => {
      setAvailableLanguages(
        (res.data || []).map(l => ({ idLanguage: l.id, name: l.name }))
      );
    }).catch(() => {
      setAvailableLanguages([
        { idLanguage: 'ES', name: 'Español' },
        { idLanguage: 'EN', name: 'English' },
        { idLanguage: 'PT', name: 'Português' },
      ]);
    });
  }, []);

  // ── Fetch team ───────────────────────────────────────────────────
  const fetchTeam = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await apiClient.get('/team/');
      setMembers(res.data);
    } catch (err) {
      setError(err.message || t('common.error_server'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { fetchTeam(); }, [fetchTeam]);

  // ── Invite modal ─────────────────────────────────────────────────
  const openInviteModal = () => {
    resetInviteForm({ name: '', email: '', idRole: 3 });
    setInviteError('');
    setShowInvite(true);
  };
  const closeInviteModal = () => { setShowInvite(false); setInviteError(''); };

  useEffect(() => {
    if (!showInvite) return;
    const h = (e) => { if (e.key === 'Escape') closeInviteModal(); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [showInvite]);

  const onInviteSubmit = async (data) => {
    setInviting(true);
    setInviteError('');
    try {
      await apiClient.post('/team/invite', {
        name: data.name.trim(),
        email: data.email.trim().toLowerCase(),
        idRole: Number(data.idRole),
      });
      closeInviteModal();
      fetchTeam();
    } catch (err) {
      setInviteError(err.message || t('common.error_server'));
    } finally {
      setInviting(false);
    }
  };

  // ── Edit name modal ──────────────────────────────────────────────
  const openEditName = (member) => {
    setEditingUser(member);
    setEditName(member.name);
    setEditLang(member.idLanguage || 'ES');
  };

  useEffect(() => {
    if (!editingUser) return;
    const h = (e) => { if (e.key === 'Escape') setEditingUser(null); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [editingUser]);

  const saveEditName = async () => {
    if (!editingUser || !editName.trim()) return;
    setEditSaving(true);
    try {
      await apiClient.put(`/team/${editingUser.idUser}/name`, {
        name: editName.trim(),
        idLanguage: editLang,
      });
      // Si edité mi propio perfil → actualizar header + idioma activo
      if (isSelf(editingUser)) {
        const updatedUser = { ...user, name: editName.trim(), idLanguage: editLang };
        localStorage.setItem('vnext_user', JSON.stringify(updatedUser));
        setUser(updatedUser);
        setLanguage(editLang.toLowerCase());
      }
      setEditingUser(null);
      fetchTeam();
    } catch (err) {
      setError(err.message || t('common.error_server'));
      setEditingUser(null);
    } finally {
      setEditSaving(false);
    }
  };

  // ── Confirm actions ──────────────────────────────────────────────
  const executeAction = async () => {
    if (!confirmAction || !confirmUser) return;
    setActionLoading(true);
    try {
      const { idUser } = confirmUser;
      if (confirmAction === 'resend') await apiClient.post(`/team/${idUser}/resend`);
      if (confirmAction === 'deactivate') await apiClient.put(`/team/${idUser}/deactivate`);
      if (confirmAction === 'activate') await apiClient.put(`/team/${idUser}/activate`);
      if (confirmAction === 'make_admin') await apiClient.put(`/team/${idUser}/role`, { idRole: 2 });
      if (confirmAction === 'make_user') await apiClient.put(`/team/${idUser}/role`, { idRole: 3 });
      fetchTeam();
    } catch (err) {
      setError(err.message || t('common.error_server'));
    } finally {
      setActionLoading(false);
      setConfirmAction(null);
      setConfirmUser(null);
    }
  };

  const openConfirm = (action, member) => { setConfirmAction(action); setConfirmUser(member); };

  const isSelf = (member) => member.email === user?.email;

  // ── Filtering + Pagination ───────────────────────────────────────
  const filteredMembers = useMemo(() => {
    let result = members;
    if (roleFilter !== 'all') result = result.filter(m => m.idRole === Number(roleFilter));
    if (searchTerm.trim()) {
      const q = searchTerm.toLowerCase();
      result = result.filter(m => m.name.toLowerCase().includes(q) || m.email.toLowerCase().includes(q));
    }
    return result;
  }, [members, roleFilter, searchTerm]);

  const totalPages = Math.ceil(filteredMembers.length / ITEMS_PER_PAGE);
  const paginatedMembers = filteredMembers.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);
  useEffect(() => { setCurrentPage(1); }, [roleFilter, searchTerm]);

  // ── Helpers ──────────────────────────────────────────────────────
  const getStatusBadge = (m) => {
    if (m.isPending) return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-50 text-amber-600 border border-amber-200">
        <Mail className="w-3 h-3" />{t('team.status.pending')}
      </span>
    );
    if (m.status === 1) return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-green-50 text-green-600 border border-green-200">
        {t('team.status.active')}
      </span>
    );
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-red-50 text-red-500 border border-red-200">
        {t('team.status.inactive')}
      </span>
    );
  };

  const getConfirmMessage = () => {
    if (!confirmAction || !confirmUser) return '';
    const name = confirmUser.name;
    return (t(`team.confirm.${confirmAction}`) || '').replace('{name}', name);
  };

  // ── Render ───────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-slate-50 font-sans pb-12">
      <AppSidebar user={user} onLogout={handleLogout} />

      {/* ── Header IDÉNTICO a Dashboard ──────────────────────────── */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-30 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <button
              onClick={openSidebar}
              aria-label="Abrir menú"
              className="p-2 rounded-xl text-slate-500 hover:text-indigo-600 hover:bg-indigo-50 transition-colors shrink-0"
            >
              <Menu className="w-5 h-5" />
            </button>
            <Link to="/dashboard" className="flex items-center cursor-pointer group shrink-0">
              <img src={Isotipo} alt="DatiaFlow"
                className="block md:hidden h-9 w-9 object-cover object-left rounded-md group-hover:scale-105 transition-transform" />
              <img src={LogoHorizontal} alt="DatiaFlow"
                className="hidden md:block h-11 w-auto group-hover:opacity-90 transition-opacity" />
            </Link>
          </div>
          <div className="flex items-center space-x-6">
            <LanguageSelector />
            <div className="flex items-center space-x-3 border-l border-slate-200 pl-6">
              <div className="text-right hidden sm:block">
                <p className="text-sm font-bold text-slate-700">{clientName}</p>
                <p className="text-xs text-slate-500">{user?.name}</p>
              </div>
              <button
                onClick={handleLogout}
                className="p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition"
                title={t('dashboard.logout')}
              >
                <LogOut className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* ── Page title + Invite ──────────────────────────────────── */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-indigo-50 text-indigo-600 rounded-xl flex items-center justify-center">
              <Users className="w-5 h-5" />
            </div>
            <h1 className="text-xl sm:text-2xl font-black text-slate-800">{t('team.title')}</h1>
          </div>
          {isAdmin && (
            <button onClick={openInviteModal}
              className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2.5 rounded-xl font-bold text-sm hover:bg-blue-700 transition-all shadow-sm">
              <UserPlus className="w-4 h-4" />
              <span className="hidden sm:inline">{t('team.invite_btn')}</span>
            </button>
          )}
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 text-red-600 text-sm font-medium rounded-xl flex items-center gap-2">
            <div className="w-1.5 h-1.5 bg-red-500 rounded-full" />{error}
            <button onClick={() => setError('')} className="ml-auto"><X className="w-4 h-4" /></button>
          </div>
        )}

        {/* ── Filters ─────────────────────────────────────────────── */}
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 mb-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-2.5 w-4 h-4 text-slate-400" />
            <input type="text" value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
              placeholder={t('team.search_placeholder') || 'Buscar por nombre o correo...'}
              className="w-full bg-white border border-slate-200 rounded-xl py-2 pl-9 pr-4 text-sm outline-none focus:border-blue-300 transition-colors" />
            {searchTerm && (
              <button onClick={() => setSearchTerm('')} className="absolute right-3 top-2.5">
                <X className="w-4 h-4 text-slate-400 hover:text-slate-600" />
              </button>
            )}
          </div>
          <div className="flex gap-1.5">
            {[
              { value: 'all', label: t('team.filter.all') || 'Todos' },
              { value: '2', label: t('team.filter.admin') || 'Admin' },
              { value: '3', label: t('team.filter.user') || 'Usuario' },
            ].map(f => (
              <button key={f.value} onClick={() => setRoleFilter(f.value)}
                className={`px-3 py-2 rounded-xl text-xs font-bold transition-colors ${roleFilter === f.value
                  ? 'bg-indigo-50 text-indigo-700 border border-indigo-200'
                  : 'bg-white text-slate-500 border border-slate-200 hover:bg-slate-50'
                  }`}>
                {f.label}
              </button>
            ))}
          </div>
        </div>

        {/* ── Table ───────────────────────────────────────────────── */}
        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-blue-500" /></div>
        ) : filteredMembers.length === 0 ? (
          <div className="text-center py-20 text-slate-400 bg-white rounded-2xl border border-slate-200">
            <Users className="w-12 h-12 mx-auto mb-3 opacity-40" />
            <p className="font-medium">{searchTerm || roleFilter !== 'all' ? (t('team.no_results') || 'Sin resultados') : t('team.empty')}</p>
          </div>
        ) : (
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200">
                    <th className="text-left px-4 sm:px-6 py-3 text-[10px] font-black text-slate-400 uppercase tracking-widest">{t('team.col.name')}</th>
                    <th className="text-left px-4 sm:px-6 py-3 text-[10px] font-black text-slate-400 uppercase tracking-widest hidden sm:table-cell">{t('team.col.email')}</th>
                    <th className="text-center px-4 py-3 text-[10px] font-black text-slate-400 uppercase tracking-widest hidden sm:table-cell">{t('team.col.language') || 'Idioma'}</th>
                    <th className="text-center px-4 py-3 text-[10px] font-black text-slate-400 uppercase tracking-widest">{t('team.col.role')}</th>
                    <th className="text-center px-4 py-3 text-[10px] font-black text-slate-400 uppercase tracking-widest">{t('team.col.status')}</th>
                    <th className="text-center px-4 py-3 text-[10px] font-black text-slate-400 uppercase tracking-widest">{t('team.col.actions') || 'Acciones'}</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedMembers.map((m) => (
                    <tr key={m.idUser} className="border-b border-slate-100 last:border-0 hover:bg-slate-50/50 transition-colors">
                      <td className="px-4 sm:px-6 py-4">
                        <div className="flex items-center gap-2">
                          <p className="font-bold text-sm text-slate-800">{m.name}</p>
                          {isSelf(m) && (
                            <span className="text-[9px] font-bold text-indigo-500 bg-indigo-50 px-1.5 py-0.5 rounded">
                              {t('team.you') || 'Tú'}
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-slate-400 sm:hidden">{m.email}</p>
                      </td>
                      <td className="px-4 sm:px-6 py-4 text-sm text-slate-500 hidden sm:table-cell">{m.email}</td>
                      <td className="px-4 py-4 text-center hidden sm:table-cell">
                        <span className="inline-block px-2 py-0.5 rounded-full text-[10px] font-bold bg-slate-50 text-slate-500 border border-slate-200">
                          {m.idLanguage || 'ES'}
                        </span>
                      </td>
                      <td className="px-4 py-4 text-center">
                        <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-bold ${m.idRole === 1 ? 'bg-purple-50 text-purple-600 border border-purple-200' :
                          m.idRole === 2 ? 'bg-blue-50 text-blue-600 border border-blue-200' :
                            'bg-slate-100 text-slate-500 border border-slate-200'
                          }`}>
                          {ROLE_LABELS[m.idRole]}
                        </span>
                      </td>
                      <td className="px-4 py-4 text-center">{getStatusBadge(m)}</td>

                      {/* ── Action buttons (inline, como Dashboard) ── */}
                      <td className="px-4 py-4">
                        <div className="flex items-center justify-center gap-1">
                          {/* Editar nombre — siempre */}
                          <button onClick={() => openEditName(m)}
                            className="p-1 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition"
                            title={t('team.action.edit_name')}>
                            <Pencil className="w-4 h-4" />
                          </button>

                          {isAdmin && (
                            <React.Fragment>
                              {/* Reenviar invitación */}
                              {m.isPending && (
                                <button onClick={() => openConfirm('resend', m)}
                                  className="p-1 text-slate-400 hover:text-amber-600 hover:bg-amber-50 rounded-lg transition"
                                  title={t('team.action.resend')}>
                                  <RefreshCw className="w-4 h-4" />
                                </button>
                              )}

                              {/* Cambiar rol — solo activos, no self */}
                              {m.status === 1 && m.idRole === 3 && !isSelf(m) && (
                                <button onClick={() => openConfirm('make_admin', m)}
                                  className="p-1 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition"
                                  title={t('team.action.make_admin')}>
                                  <ShieldCheck className="w-4 h-4" />
                                </button>
                              )}
                              {m.status === 1 && m.idRole === 2 && !isSelf(m) && (
                                <button onClick={() => openConfirm('make_user', m)}
                                  className="p-1 text-slate-400 hover:text-orange-600 hover:bg-orange-50 rounded-lg transition"
                                  title={t('team.action.make_user')}>
                                  <ShieldOff className="w-4 h-4" />
                                </button>
                              )}

                              {/* Desactivar — solo activos, no self */}
                              {m.status === 1 && !isSelf(m) && (
                                <button onClick={() => openConfirm('deactivate', m)}
                                  className="p-1 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition"
                                  title={t('team.action.deactivate')}>
                                  <UserX className="w-4 h-4" />
                                </button>
                              )}

                              {/* Reactivar — solo inactivos con password */}
                              {m.status === 0 && !m.isPending && (
                                <button onClick={() => openConfirm('activate', m)}
                                  className="p-1 text-slate-400 hover:text-green-600 hover:bg-green-50 rounded-lg transition"
                                  title={t('team.action.activate')}>
                                  <UserCheck className="w-4 h-4" />
                                </button>
                              )}
                            </React.Fragment>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* ── Pagination ──────────────────────────────────────── */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between px-4 sm:px-6 py-3 border-t border-slate-100">
                <p className="text-xs text-slate-400">
                  {filteredMembers.length} {t('team.pagination.total') || 'usuarios'}
                </p>
                <div className="flex items-center gap-1">
                  <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}
                    className="p-1.5 rounded-lg hover:bg-slate-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors">
                    <ChevronLeft className="w-4 h-4 text-slate-500" />
                  </button>
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                    <button key={page} onClick={() => setCurrentPage(page)}
                      className={`w-8 h-8 rounded-lg text-xs font-bold transition-colors ${page === currentPage ? 'bg-indigo-50 text-indigo-700 border border-indigo-200' : 'text-slate-500 hover:bg-slate-100'
                        }`}>
                      {page}
                    </button>
                  ))}
                  <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}
                    className="p-1.5 rounded-lg hover:bg-slate-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors">
                    <ChevronRight className="w-4 h-4 text-slate-500" />
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── Invite Modal (RHF + Zod) ─────────────────────────────── */}
      {showInvite && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40 p-4" onClick={closeInviteModal}>
          <div className="bg-white w-full max-w-md rounded-2xl shadow-2xl animate-in zoom-in-95 duration-200" onClick={e => e.stopPropagation()}>
            <div className="p-6 border-b border-slate-100">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <UserPlus className="w-5 h-5 text-blue-600" />
                  <h3 className="text-lg font-black text-slate-800">{t('team.invite_title')}</h3>
                </div>
                <button onClick={closeInviteModal} className="p-1 hover:bg-slate-100 rounded-lg">
                  <X className="w-5 h-5 text-slate-400" />
                </button>
              </div>
            </div>
            <form onSubmit={handleSubmit(onInviteSubmit)} noValidate>
              <div className="p-6 space-y-4">
                {inviteError && (
                  <div className="p-3 bg-red-50 text-red-600 text-xs font-bold rounded-xl">{inviteError}</div>
                )}
                <div>
                  <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1 mb-2">{t('team.field.name')} *</label>
                  <input type="text" {...register('name')} maxLength={45}
                    className={`w-full bg-slate-50 border-2 rounded-xl py-2.5 px-4 outline-none font-medium text-sm transition-all ${formErrors.name ? 'border-red-300 focus:border-red-500' : 'border-transparent focus:border-blue-100'}`}
                    placeholder={t('team.field.name_placeholder')} />
                  {formErrors.name && <span className="text-red-500 text-[10px] font-bold ml-2 mt-1 block">{formErrors.name.message}</span>}
                </div>
                <div>
                  <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1 mb-2">{t('team.field.email')} *</label>
                  <input type="email" {...register('email')}
                    className={`w-full bg-slate-50 border-2 rounded-xl py-2.5 px-4 outline-none font-medium text-sm transition-all ${formErrors.email ? 'border-red-300 focus:border-red-500' : 'border-transparent focus:border-blue-100'}`}
                    placeholder="user@company.com" />
                  {formErrors.email && <span className="text-red-500 text-[10px] font-bold ml-2 mt-1 block">{formErrors.email.message}</span>}
                </div>
                <div>
                  <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1 mb-2">{t('team.field.role')}</label>
                  <select {...register('idRole', { valueAsNumber: true })}
                    className="w-full bg-slate-50 border-2 border-transparent focus:border-blue-100 rounded-xl py-2.5 px-4 outline-none font-medium text-sm cursor-pointer">
                    <option value={2}>Admin — {t('team.role_desc.admin')}</option>
                    <option value={3}>User — {t('team.role_desc.user')}</option>
                  </select>
                </div>
              </div>
              <div className="p-6 border-t border-slate-100 flex justify-end gap-3">
                <button type="button" onClick={closeInviteModal}
                  className="px-4 py-2.5 rounded-xl text-sm font-bold text-slate-500 hover:bg-slate-100 transition-colors">
                  {t('common.buttons.cancel') || 'Cancelar'}
                </button>
                <button type="submit" disabled={inviting}
                  className="flex items-center gap-2 bg-blue-600 text-white px-5 py-2.5 rounded-xl font-bold text-sm hover:bg-blue-700 transition-all disabled:bg-slate-300">
                  {inviting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  {t('team.invite_send')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Edit Name Modal ──────────────────────────────────────── */}
      {editingUser && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40 p-4" onClick={() => setEditingUser(null)}>
          <div className="bg-white w-full max-w-sm rounded-2xl shadow-2xl animate-in zoom-in-95 duration-200" onClick={e => e.stopPropagation()}>
            <div className="p-6 border-b border-slate-100">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Pencil className="w-4 h-4 text-blue-600" />
                  <h3 className="text-lg font-black text-slate-800">{t('team.action.edit_name')}</h3>
                </div>
                <button onClick={() => setEditingUser(null)} className="p-1 hover:bg-slate-100 rounded-lg">
                  <X className="w-5 h-5 text-slate-400" />
                </button>
              </div>
            </div>
            <div className="p-6">
              <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1 mb-2">{t('team.field.name')}</label>
              <input type="text" value={editName} onChange={e => setEditName(e.target.value)}
                maxLength={45}
                className="w-full bg-slate-50 border-2 border-transparent focus:border-blue-100 rounded-xl py-2.5 px-4 outline-none font-medium text-sm"
                autoFocus
                onKeyDown={e => { if (e.key === 'Enter' && editName.trim()) saveEditName(); }} />
              <p className="text-[10px] text-slate-400 mt-1 ml-1">{editName.length}/45</p>
              <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1 mb-2 mt-4">
                {t('team.field.language') || 'Idioma'}
              </label>
              <select
                value={editLang}
                onChange={e => setEditLang(e.target.value)}
                className="w-full bg-slate-50 border-2 border-transparent focus:border-blue-100 rounded-xl py-2.5 px-4 outline-none font-medium text-sm cursor-pointer"
              >
                {availableLanguages.map(lang => (
                  <option key={lang.idLanguage} value={lang.idLanguage}>
                    {lang.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="p-6 border-t border-slate-100 flex justify-end gap-3">
              <button onClick={() => setEditingUser(null)}
                className="px-4 py-2.5 rounded-xl text-sm font-bold text-slate-500 hover:bg-slate-100">
                {t('common.buttons.cancel') || 'Cancelar'}
              </button>
              <button onClick={saveEditName} disabled={editSaving || !editName.trim()}
                className="flex items-center gap-2 bg-blue-600 text-white px-5 py-2.5 rounded-xl font-bold text-sm hover:bg-blue-700 disabled:bg-slate-300">
                {editSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : t('common.buttons.accept') || 'Guardar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Confirmation Modal ───────────────────────────────────── */}
      <Modal
        isOpen={!!confirmAction}
        title={t('common.confirm') || 'Confirmar'}
        message={getConfirmMessage()}
        onAccept={executeAction}
        onClose={() => { setConfirmAction(null); setConfirmUser(null); }}
        showCancel={true}
      />
    </div>
  );
};
